import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useSelector } from 'react-redux';

import scribeTrack from 'scribe/scribe-track';
import instrumentationDuck from 'ducks/instrumentation-duck';
import { useFeatureFlag } from 'context/feature-flag';
import { inGrabApp, isMobile } from 'common/utils/platform';
import { isCashless } from 'common/utils/payment';
import { PICKING_UP, DROPPING_OFF } from 'constants/order-status';
import {
  getExpectationMessage,
  getCashExpectation,
  getCashlessExpectation,
  getNonBookerCashlessExpectation,
  getFallbackCashExpectation,
} from 'common/utils/expectation-management';
import InfoBanner from '../banner/info-banner';
import InstallAppButton from '../button/install-app-button';

import walletSvg from '../../public/images/wallet.svg';
import styles from './expectation-management.less';

const ExpectationManagement = ({ id, payment, status }) => {
  const { orderId } = useSelector(
    instrumentationDuck.selectors.instrumentation,
  );
  const { hideCashlessPOD } = useFeatureFlag();
  const showInstall = isMobile() && !inGrabApp();
  const cashless = isCashless(payment.payOnDelivery);

  const fallbackCashExpectation = useMemo(
    () => {
      const show =
        hideCashlessPOD &&
        (status === DROPPING_OFF || status === PICKING_UP) &&
        !!parseFloat(payment.total);
      if (!show) return null;

      return getFallbackCashExpectation({
        total: payment.total,
        symbol: payment.symbol,
      });
    },
    [hideCashlessPOD, status, payment],
  );

  const cashExpectation = useMemo(
    () => {
      const show =
        !hideCashlessPOD &&
        !cashless &&
        (status === DROPPING_OFF || status === PICKING_UP) &&
        payment.payOnDelivery &&
        !!parseFloat(payment.payOnDelivery.totalCashAmount);
      if (!show) return null;

      const total = payment.payOnDelivery.totalCashAmount;
      const expectation = getCashExpectation({
        total: payment.payOnDelivery.totalCashAmount,
        symbol: payment.symbol,
      });

      return {
        ...expectation,
        total,
        icon: walletSvg,
      };
    },
    [hideCashlessPOD, status, payment, cashless],
  );

  const cashlessExpectation = useMemo(
    () => {
      const show =
        !hideCashlessPOD &&
        cashless &&
        status === DROPPING_OFF &&
        payment.payOnDelivery &&
        !payment.payOnDelivery.cashlessPaymentCompleted &&
        !!parseFloat(payment.payOnDelivery.totalCashlessAmount);
      if (!show) return null;

      const total = payment.payOnDelivery.totalCashlessAmount;
      const expectation = getCashlessExpectation({
        total,
        symbol: payment.symbol,
      });

      return {
        ...expectation,
        total,
        icon: walletSvg,
      };
    },
    [hideCashlessPOD, status, payment, cashless],
  );

  const nonBookerCashlessExpectation = useMemo(
    () => {
      const show =
        !hideCashlessPOD &&
        cashless &&
        showInstall &&
        status === DROPPING_OFF &&
        payment.payOnDelivery &&
        !payment.payOnDelivery.cashlessPaymentCompleted &&
        !!parseFloat(payment.payOnDelivery.totalCashlessAmount);
      if (!show) return null;

      const total = payment.payOnDelivery.totalCashlessAmount;
      const expectation = getNonBookerCashlessExpectation({
        total,
        symbol: payment.symbol,
      });

      return {
        ...expectation,
        total,
        icon: walletSvg,
        button: (
          <InstallAppButton total={payment.payOnDelivery.totalCashlessAmount} />
        ),
      };
    },
    [hideCashlessPOD, showInstall, status, payment, cashless],
  );

  const expectation = useMemo(
    () => {
      if (fallbackCashExpectation) {
        return fallbackCashExpectation;
      }

      if (nonBookerCashlessExpectation) {
        return nonBookerCashlessExpectation;
      }

      if (cashlessExpectation) {
        return cashlessExpectation;
      }

      if (cashExpectation) {
        return cashExpectation;
      }

      return getExpectationMessage(id);
    },
    [
      id,
      fallbackCashExpectation,
      nonBookerCashlessExpectation,
      cashlessExpectation,
      cashExpectation,
    ],
  );

  useEffect(
    () => {
      if (expectation && expectation.total && !hideCashlessPOD) {
        scribeTrack.expressAcquisitionBanner.podReminder({
          orderIdDisplayed: orderId,
          isInApp: !showInstall,
          amount: expectation.total,
        });
      }
    },
    [orderId, hideCashlessPOD, expectation],
  );

  if (!expectation) return null;

  if (hideCashlessPOD) {
    const containerClassName = cx(styles.container, {
      [styles.noTextBody]: !expectation.body,
    });

    return (
      <div className={containerClassName}>
        {expectation.title && (
          <p className={styles.title}>{expectation.title}</p>
        )}
        {expectation.body && <p className={styles.body}>{expectation.body}</p>}
      </div>
    );
  }

  return (
    <InfoBanner
      icon={expectation.icon}
      titleStyle={{
        fontWeight: 'bold',
      }}
      text={expectation}
      button={expectation.button}
    />
  );
};

ExpectationManagement.propTypes = {
  id: PropTypes.number,
  payment: PropTypes.shape({
    symbol: PropTypes.string,
    codAmount: PropTypes.bool,
    payment: PropTypes.shape({
      cod: PropTypes.string,
      symbol: PropTypes.string,
      deliveryFee: PropTypes.string,
      total: PropTypes.string,
      payOnDelivery: PropTypes.shape({
        items: PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.string,
            amount: PropTypes.string,
            paymentMethod: PropTypes.string,
          }),
        ),
        totalAmount: PropTypes.string,
        totalCashAmount: PropTypes.string,
        totalCashlessAmount: PropTypes.string,
        cashlessPaymentCompleted: PropTypes.bool,
      }),
    }),
  }),
  status: PropTypes.string,
};

ExpectationManagement.defaultProps = {
  id: 0,
  payment: {
    cod: '',
    symbol: '',
    deliveryFee: '',
    total: '',
    payOnDelivery: {
      items: [],
      totalAmount: '',
      totalCashAmount: '',
      totalCashlessAmount: '',
      cashlessPaymentCompleted: false,
    },
  },
  status: '',
};

export default ExpectationManagement;
